import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  serverTimestamp,
  DocumentData,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { linkActiveEvent, setConnectionPhase, setConnectionPhaseStatus } from './connectionJourneyService';

export type MeetingProposal = {
  id?: string;
  connectionId: string;
  phase: 'call' | 'video' | 'physical';
  createdBy: string;
  createdFor: string;
  suggestedSlots: { startTime: Date; endTime: Date }[];
  status: 'pending' | 'accepted' | 'rejected' | 'countered';
  message?: string;
  createdAt: Date;
  updatedAt: Date;
};

const col = (connectionId: string) => collection(db, 'connections', connectionId, 'proposals');

export async function createProposal(
  connectionId: string,
  createdBy: string,
  createdFor: string,
  phase: 'call' | 'video' | 'physical',
  slots: { startTime: Date; endTime: Date }[],
  message?: string
) {
  if (!connectionId || !createdBy || !createdFor || slots.length === 0) return;

  // Validate slots: max 3, end>start, min duration 20m, unique, sorted
  const validatedSlots = slots
    .filter((s) => s.startTime && s.endTime)
    .map((s) => ({
      startTime: new Date(s.startTime),
      endTime: new Date(s.endTime),
    }))
    .filter((s) => !isNaN(s.startTime.getTime()) && !isNaN(s.endTime.getTime()))
    .filter((s) => s.endTime.getTime() - s.startTime.getTime() >= 20 * 60_000)
    .filter((s) => s.endTime > s.startTime);

  const unique = new Map<string, { startTime: Date; endTime: Date }>();
  validatedSlots.forEach((s) => {
    const key = `${s.startTime.toISOString()}-${s.endTime.toISOString()}`;
    if (!unique.has(key)) unique.set(key, s);
  });

  const finalSlots = Array.from(unique.values()).sort((a, b) => a.startTime.getTime() - b.startTime.getTime()).slice(0, 3);
  if (finalSlots.length === 0) return;

  const payload: Omit<MeetingProposal, 'id'> = {
    connectionId,
    phase,
    createdBy,
    createdFor,
    suggestedSlots: finalSlots,
    status: 'pending',
    message: message ?? undefined,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  return addDoc(col(connectionId), {
    ...payload,
    suggestedSlots: finalSlots.map((s) => ({
      startTime: s.startTime,
      endTime: s.endTime,
    })),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function listenProposals(connectionId: string, userId: string, cb: (proposals: MeetingProposal[]) => void) {
  if (!connectionId || !userId) return () => {};
  const q = query(col(connectionId), where('createdFor', '==', userId), where('status', 'in', ['pending', 'countered']));
  const unsub = onSnapshot(q, (snap) => {
    const items: MeetingProposal[] = snap.docs.map((d) => {
      const data = d.data() as DocumentData;
      return {
        id: d.id,
        connectionId,
        phase: data.phase || 'call',
        createdBy: data.createdBy,
        createdFor: data.createdFor,
        suggestedSlots: (data.suggestedSlots || []).map((s: any) => ({
          startTime: s.startTime?.toDate ? s.startTime.toDate() : new Date(s.startTime),
          endTime: s.endTime?.toDate ? s.endTime.toDate() : new Date(s.endTime),
        })),
        status: data.status || 'pending',
        message: data.message,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
      };
    });
    // sort by createdAt desc so newest first
    items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    cb(items);
  });
  return unsub;
}

export async function acceptProposal(
  connectionId: string,
  proposal: MeetingProposal,
  chosenSlot: { startTime: Date; endTime: Date },
  participants: string[],
  participantNames: string[]
) {
  if (!connectionId || !proposal?.id) return;
  // Guard: block if there is already an active/scheduled meeting
  const connSnap = await getDoc(doc(db, 'connections', connectionId));
  if (connSnap.exists()) {
    const data = connSnap.data();
    const hasActive =
      data.activeEventId &&
      ['scheduled', 'ready', 'in_progress'].includes(data.phaseStatus || '');
    if (hasActive) {
      const err: any = new Error('ACTIVE_MEETING_EXISTS');
      throw err;
    }
  }

  const ref = doc(db, 'connections', connectionId, 'proposals', proposal.id);

  // Idempotent: only proceed if pending/countered
  if (proposal.status === 'accepted') return;

  await updateDoc(ref, { status: 'accepted', updatedAt: serverTimestamp() });

  // Create event
  const eventData = {
    title: 'Call via voorstel',
    description: 'Geaccepteerd voorstel',
    startTime: chosenSlot.startTime,
    endTime: chosenSlot.endTime,
    type: 'call',
    category: 'work' as const,
    source: 'superkonnected',
    participants,
    participantNames,
    status: 'confirmed' as const,
    createdBy: proposal.createdFor,
    createdAt: serverTimestamp(),
    reminderEnabled: true,
    reminderMinutesBefore: 10,
    reminderSent: false,
    connectionId,
    phase: 'call' as const,
    lockedToConnectionFlow: true,
    proposalId: proposal.id,
  };

  const { addDoc, collection: fsCollection } = await import('firebase/firestore');
  const docRef = await addDoc(fsCollection(db, 'calendarEvents'), eventData);

  await linkActiveEvent(connectionId, docRef.id);
  await setConnectionPhaseStatus(connectionId, 'scheduled');
  await setConnectionPhase(connectionId, 'call', 'scheduled');

  // Notify both parties with full slot description
  const rangeText = `${chosenSlot.startTime.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  })} ${chosenSlot.startTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - ${chosenSlot.endTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;

  const notificationPayload = {
    type: 'meeting_confirmed' as const,
    title: '📅 Afspraak ingepland',
    message: `Call gepland: ${rangeText}`,
    data: {
      connectionId,
      eventId: docRef.id,
      start: chosenSlot.startTime,
      end: chosenSlot.endTime,
      phase: 'call'
    },
    read: false,
    createdAt: serverTimestamp()
  };

  // Sender (createdBy) and receiver (createdFor)
  await addDoc(fsCollection(db, 'notifications'), {
    ...notificationPayload,
    userId: proposal.createdBy,
    message: `Call gepland met ${participantNames[1] || 'connectie'}: ${rangeText}`,
    subtitle: rangeText
  });
  await addDoc(fsCollection(db, 'notifications'), {
    ...notificationPayload,
    userId: proposal.createdFor,
    message: `Call gepland met ${participantNames[0] || 'connectie'}: ${rangeText}`,
    subtitle: rangeText
  });
}

export async function rejectProposal(connectionId: string, proposalId: string) {
  if (!connectionId || !proposalId) return;
  const ref = doc(db, 'connections', connectionId, 'proposals', proposalId);
  await updateDoc(ref, { status: 'rejected', updatedAt: serverTimestamp() });
}

export async function counterProposal(connectionId: string, proposalId: string, slots: { startTime: Date; endTime: Date }[], message?: string) {
  if (!connectionId || !proposalId || slots.length === 0) return;
  const ref = doc(db, 'connections', connectionId, 'proposals', proposalId);
  await updateDoc(ref, {
    status: 'countered',
    suggestedSlots: slots,
    message: message || '',
    updatedAt: serverTimestamp(),
  });
}
